export type JobCostingCompletionStatus =
  | "NOT_STARTED"
  | "IN_PROGRESS"
  | "WITHIN_THRESHOLD"
  | "OUT_OF_THRESHOLD";

export type JobCostingVisibilityMetrics = {
  hasJobCostsEntered: boolean;
  jobCostingCompletionStatus: JobCostingCompletionStatus;
  jobCostingVariancePercent: number;
  jobCostingThresholdPercent: number;
  jobCostEntryDaysCount: number;
  lastJobCostEntryDate: string | null;
  missingCostCategories: string[];
};

type DeriveJobCostingMetricsInput = {
  actualCostAdditional: string | number;
  actualCostEquipment: string | number;
  actualCostLabour: string | number;
  actualCostMaterial: string | number;
  actualCostTotal: string | number;
  actualCostTransferTruck: string | number;
  proposedCostAdditional: string | number;
  proposedCostEquipment: string | number;
  proposedCostLabour: string | number;
  proposedCostMaterial: string | number;
  proposedCostTotal: string | number;
  proposedCostTransferTruck: string | number;
  costItems?: Array<{ date?: string | null } | null> | null;
  thresholdPercent: number;
};

export const DEFAULT_JOB_COSTING_THRESHOLD_PERCENT = 10;

const toNumber = (value: string | number | null | undefined): number => Number(value ?? 0);

export function deriveJobCostingVisibilityMetrics(
  input: DeriveJobCostingMetricsInput
): JobCostingVisibilityMetrics {
  const proposedCostTotal = toNumber(input.proposedCostTotal);
  const actualCostTotal = toNumber(input.actualCostTotal);
  const dates = Array.from(
    new Set(
      (input.costItems ?? [])
        .map((costItem) => costItem?.date?.slice(0, 10))
        .filter((date): date is string => Boolean(date))
    )
  ).sort();
  const missingCostCategories = [
    ["material", toNumber(input.proposedCostMaterial), toNumber(input.actualCostMaterial)],
    ["labour", toNumber(input.proposedCostLabour), toNumber(input.actualCostLabour)],
    ["equipment", toNumber(input.proposedCostEquipment), toNumber(input.actualCostEquipment)],
    ["additional", toNumber(input.proposedCostAdditional), toNumber(input.actualCostAdditional)],
    [
      "transfer_truck",
      toNumber(input.proposedCostTransferTruck),
      toNumber(input.actualCostTransferTruck),
    ],
  ]
    .filter(([, proposed, actual]) => proposed > 0 && actual === 0)
    .map(([category]) => category as string);

  const hasJobCostsEntered = dates.length > 0;
  const variancePercent =
    proposedCostTotal === 0
      ? 0
      : Number((((Math.abs(actualCostTotal - proposedCostTotal) / proposedCostTotal) * 100) || 0).toFixed(2));

  let status: JobCostingCompletionStatus = "NOT_STARTED";

  if (hasJobCostsEntered) {
    if (proposedCostTotal === 0) {
      status = actualCostTotal === 0 ? "NOT_STARTED" : "IN_PROGRESS";
    } else if (missingCostCategories.length > 0) {
      status = "IN_PROGRESS";
    } else if (variancePercent <= input.thresholdPercent) {
      status = "WITHIN_THRESHOLD";
    } else {
      status = "OUT_OF_THRESHOLD";
    }
  }

  return {
    hasJobCostsEntered,
    jobCostingCompletionStatus: status,
    jobCostingVariancePercent: variancePercent,
    jobCostingThresholdPercent: input.thresholdPercent,
    jobCostEntryDaysCount: dates.length,
    lastJobCostEntryDate: dates.at(-1) ?? null,
    missingCostCategories,
  };
}

export function getJobCostingStatusMeta(status: JobCostingCompletionStatus) {
  switch (status) {
    case "WITHIN_THRESHOLD":
      return { label: "Within Threshold", chipColor: "#2e7d32", chipBackground: "#e8f5e9" };
    case "OUT_OF_THRESHOLD":
      return { label: "Out of Threshold", chipColor: "#c62828", chipBackground: "#ffebee" };
    case "IN_PROGRESS":
      return { label: "In Progress", chipColor: "#ed6c02", chipBackground: "#fff3e0" };
    case "NOT_STARTED":
    default:
      return { label: "Not Started", chipColor: "#c62828", chipBackground: "#ffebee" };
  }
}

export function formatMissingCostCategory(category: string): string {
  return category
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
